<template>
  <ButtonDropdown v-model="visible" color="input" :label="rangeText" outline :size="size">
    <div class="popup-content q-pa-sm">
      <q-list dense padding>
        <q-item-label header>{{ $t('Quick select') }}</q-item-label>
        <q-item
          :active="selected === item.value"
          clickable
          v-ripple
          v-for="(item, key) in items"
          :key="key"
        >
          <q-item-section @click="handleClick(item.value)">
            <span class="text-bold">{{ item.label }}</span>
          </q-item-section>
        </q-item>
      </q-list>
      <div>
        <div class="range-input q-mb-md">
          <div class="input-box">
            <q-input ref="start" :value="inputStart" label="From" dense @change="onChangeStart" />
          </div>
          <div class="separator q-py-none q-px-lg">-</div>
          <div class="input-box">
            <q-input ref="end" :value="inputEnd" label="To" dense @change="onChangeEnd" />
          </div>
        </div>
        <div class="date-picker">
          <VDatePicker
            v-model="currentRange"
            is-range
            is-inline
            :columns="2"
            :max-date="maxDate"
            :min-date="minDate"
            :locale="language"
          />
        </div>
      </div>
    </div>
    <div class="footer q-pa-sm">
      <Button :label="$t('Cancel')" color="standard" @click="visible = false" />
      <Button :label="$t('Apply')" class="q-ml-md" color="primary" @click="handleSubmit" />
    </div>
  </ButtonDropdown>
</template>

<script>
/**
 * Props
 * range: {start:2020/01/01,end:2020/01/31}
 *
 * Return
 * {start:2020/01/01,end:2020/01/31}
 **/
import moment from 'moment';
import Vue from 'vue';
import VDatePicker from 'v-calendar/lib/components/date-picker.umd';
import Button from './Button';
import ButtonDropdown from './ButtonDropdown';

Vue.component('date-picker', VDatePicker);

const messages = {
  en: {
    'Quick select': 'Quick select',
    'Month to date': 'Month to date',
    'Year to date': 'Year to date',
    'Last month': 'Last month',
    'Last 7 days': 'Last 7 days',
    'Last 14 days': 'Last 14 days',
    'Last 30 days': 'Last 30 days',
    'Last 90 days': 'Last 90 days',
    'Last 180 days': 'Last 180 days',
    'Last 3 months': 'Last 3 months',
    'Last 6 months': 'Last 6 months',
    'Last 1 year': 'Last 1 year',
    Apply: 'Apply',
    Cancel: 'Cancel',
  },
  ja: {
    'Quick select': 'クイック選択',
    'Month to date': '今月',
    'Year to date': '今年',
    'Last month': '先月',
    'Last 7 days': '過去7日間',
    'Last 14 days': '過去14日間',
    'Last 30 days': '過去30日間',
    'Last 90 days': '過去90日間',
    'Last 180 days': '過去180日間',
    'Last 3 months': '過去3ヶ月',
    'Last 6 months': '過去6ヶ月',
    'Last 1 year': '過去1年',
    Apply: '適用',
    Cancel: 'キャンセル',
  },
};

const convertDate = (val) => {
  return moment(val).format('YYYY/MM/DD');
};

export default {
  name: 'DateRange',
  i18n: { messages },
  components: { VDatePicker, Button, ButtonDropdown },
  props: {
    range: {
      type: Object,
      required: true,
      default: () => {
        return {};
      },
    },
    language: {
      type: String,
      default: 'en',
    },
    size: {
      type: String,
      default: 'md',
    },
    quickRangeOptions: {
      type: Array,
    },
    minDate: {
      type: Date,
    },
    maxDate: {
      type: Date,
    },
  },
  data() {
    return {
      selected: '',
      inputStart: '',
      inputEnd: '',
      invalidStart: false,
      invalidEnd: false,
      visible: false,
      currentRange: {},
    };
  },
  mounted() {
    this.currentRange = this.range;
  },
  computed: {
    rangeText() {
      return `${convertDate(this.currentRange.start)} ~ ${convertDate(this.currentRange.end)}`;
    },
    items() {
      const optionList = [
        { label: this.$t('Month to date'), value: 'mtd' },
        { label: this.$t('Year to date'), value: 'ytd' },
        { label: this.$t('Last 7 days'), value: '7days' },
        { label: this.$t('Last 14 days'), value: '14days' },
        { label: this.$t('Last 30 days'), value: '30days' },
        { label: this.$t('Last 90 days'), value: '90days' },
        { label: this.$t('Last 180 days'), value: '180days' },
        { label: this.$t('Last month'), value: 'lastMonth' },
        { label: this.$t('Last 3 months'), value: '3months' },
        { label: this.$t('Last 6 months'), value: '6months' },
        { label: this.$t('Last 1 year'), value: '1year' },
      ];

      const options = [];

      for (const option of this.quickRangeOptions) {
        const found = optionList.find((item) => item.value === option);
        if (found) options.push(found);
      }

      return options;
    },
  },
  watch: {
    currentRange: {
      handler: function (val) {
        this.inputStart = convertDate(val.start);
        this.inputEnd = convertDate(val.end);
      },
      deep: true,
    },
  },
  methods: {
    ytd() {
      const start = moment(this.maxDate).startOf('year').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    mtd() {
      const start = moment(this.maxDate).startOf('month').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    days7() {
      const start = moment(this.maxDate).subtract(7, 'days').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    days14() {
      const start = moment(this.maxDate).subtract(14, 'days').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    days30() {
      const start = moment(this.maxDate).subtract(30, 'days').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    days90() {
      const start = moment(this.maxDate).subtract(90, 'days').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    days180() {
      const start = moment(this.maxDate).subtract(180, 'days').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    lastMonth() {
      const start = moment().subtract(1, 'months').startOf('month').toDate();
      const end = moment().subtract(1, 'months').endOf('month').toDate();

      return { start: start, end: end };
    },

    month3() {
      const start = moment().subtract(3, 'months').startOf('month').toDate();
      const end = moment().subtract(1, 'months').endOf('month').toDate();

      return {
        start: start,
        end: end,
      };
    },

    month6() {
      const start = moment().subtract(6, 'months').startOf('month').toDate();
      const end = moment().subtract(1, 'months').endOf('month').toDate();

      return {
        start: start,
        end: end,
      };
    },

    year1() {
      const start = moment(this.maxDate).subtract(12, 'month').startOf('month').toDate();
      const end = this.maxDate;

      return {
        start: start,
        end: end,
      };
    },

    handleClick(val) {
      let range;

      switch (val) {
        case 'ytd':
          range = this.ytd();
          break;

        case 'mtd':
          range = this.mtd();
          break;

        case '7days':
          range = this.days7();
          break;

        case '14days':
          range = this.days14();
          break;

        case '30days':
          range = this.days30();
          break;

        case '90days':
          range = this.days90();
          break;

        case '180days':
          range = this.days180();
          break;

        case 'lastMonth':
          range = this.lastMonth();
          break;

        case '1month':
          range = this.month1();
          break;

        case '3months':
          range = this.month3();
          break;

        case '6months':
          range = this.month6();
          break;

        case '1year':
          range = this.year1();
          break;
      }

      this.currentRange = range;
      this.inputStart = convertDate(range.start);
      this.inputEnd = convertDate(range.end);
      this.selected = val;
    },

    onChangeStart(e) {
      let start = moment.utc(e.target.value);
      if (!start._isValid) {
        return;
      }
      if (start < this.minDate) {
        start = moment(this.currentRange.start);
      }
      this.currentRange = {
        ...this.currentRange,
        start: start.toDate(),
      };
    },

    onChangeEnd(e) {
      let end = moment.utc(e.target.value);
      if (!end._isValid) {
        return;
      }
      if (end > this.maxDate) {
        end = moment(this.currentRange.end);
      }
      this.currentRange = {
        ...this.currentRange,
        end: end.toDate(),
      };
    },

    handleSubmit() {
      this.$emit('change', this.currentRange);
      this.visible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.popup-content {
  display: flex;

  .buttons {
    display: flex;
    flex-wrap: wrap;
  }

  .vc-container {
    border: none;
  }
}

.range-input {
  display: flex;
  align-items: center;
}

.input-box {
  width: 100%;
}

.separator {
  font-size: 1.5em;
}

.footer {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  border-top: 1px solid #e0e0e0;
}
</style>
